import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import type { Editor } from "@tiptap/react";
import noop from "lodash/noop";
import { TextSelectDropdown } from "@circle-react-shared/TextSelectDropdown";
import { useTextSource } from "@circle-react-uikit/InsertVariableButton/useTextSource";

export interface Variable {
  group: string;
  label: string;
  value: string;
}

const DROPDOWN_TRIGGER_INPUT_TEXT = "{{";

const URL_VARIABLES = [
  "{{community.url}}",
  "{{trigger.event.url}}",
  "{{trigger.event.space.url}}",
  "{{trigger.event.space.space_group.url}}",
  "{{trigger.comment.url}}",
  "{{trigger.comment.post.url}}",
  "{{trigger.comment.post.space.url}}",
  "{{trigger.comment.post.space.space_group.url}}",
  "{{trigger.post.url}}",
  "{{trigger.post.space.url}}",
  "{{trigger.post.space.space_group.url}}",
];

export const InsertVariableButtonWrapper = ({
  editor,
  inputRef,
  setInputChangeHandler = noop,
  variables,
  wrapperClassName = "max-w-sm pt-3",
  optionsClassName,
  enableLinkInsertion = false,
  children,
  shouldRenderPortal = false,
}: {
  editor?: Editor;
  inputRef?: React.RefObject<HTMLInputElement>;
  setInputChangeHandler?: (handler: () => void) => void;
  variables: Variable[];
  wrapperClassName?: string;
  optionsClassName?: string;
  enableLinkInsertion?: boolean;
  children: ReactNode;
  shouldRenderPortal?: boolean;
}) => {
  const textSource = useTextSource({ editor, inputRef });
  const isLinkInsertionSupported =
    Boolean(editor) && Boolean(enableLinkInsertion);

  const getLastTwoChars = () => {
    if (!textSource) {
      return "";
    }

    const textUpToAnchor = textSource.textUpToAnchor();

    if (textUpToAnchor.length >= 2) {
      return textUpToAnchor.slice(-2);
    }

    return textUpToAnchor;
  };

  const handleInsertVariable = (variableName: string) => {
    const shouldSliceVariableName =
      getLastTwoChars() === DROPDOWN_TRIGGER_INPUT_TEXT;
    const textToInsert = shouldSliceVariableName
      ? variableName.slice(2)
      : variableName;

    const isUrlType = URL_VARIABLES.includes(variableName);
    if (isLinkInsertionSupported && isUrlType) {
      textSource?.insertLink(variableName, shouldSliceVariableName);
    } else {
      textSource?.insertText(textToInsert);
    }
  };

  const insertVariableButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const handleContentChange = () => {
      const shouldShowDmOptions =
        getLastTwoChars() === DROPDOWN_TRIGGER_INPUT_TEXT;
      if (shouldShowDmOptions && insertVariableButtonRef.current) {
        insertVariableButtonRef.current.click();
      }
    };

    if (editor) {
      editor.on("update", handleContentChange);
    } else {
      setInputChangeHandler(handleContentChange);
    }

    return () => {
      if (editor) {
        editor.off("update", handleContentChange);
      } else {
        setInputChangeHandler(noop);
      }
    };
  }, [textSource]);

  return (
    <div className={wrapperClassName}>
      <TextSelectDropdown
        options={variables}
        groupBy={(option: any) => option.group}
        onSelect={(value: unknown) => handleInsertVariable(value as string)}
        buttonRef={insertVariableButtonRef}
        optionsClassName={optionsClassName}
        shouldRenderPortal={shouldRenderPortal}
      >
        {children}
      </TextSelectDropdown>
    </div>
  );
};
